/**
 * API Client Module
 */

// 如果环境变量为空，则使用当前域名
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || window.location.origin;

// Export for use in authUtils
function getApiBaseUrl() {
  return API_BASE_URL;
}

// 用户角色
type UserRole = 'user' | 'admin' | 'super_admin';

interface LoginResponse {
  access_token: string;
  refresh_token?: string;
  expires_in?: number;
  authorization_token?: string;
  auth_request_id?: string;
  redirect_uri?: string;
  requires_2fa: boolean;
  pending_token?: string;
  must_reset_password: boolean;
  must_bind_2fa: boolean;
  must_bind_phone: boolean;
  /** @deprecated 后端不再下发自增 id（可枚举），新版本恒为 undefined */
  account_id?: number;
  /** 对外公开 ID（U0XXXXXX），用它做会话标识与展示 */
  display_id?: string;
  account_type: string;
  user_email?: string;
  user_nickname?: string;
  user_role?: UserRole;
}

// SMS provider config (admin). Backend JSON uses Go field names (capitalized).
export interface SMSConfigResponse {
  Mode: string;
  DebugExposeCode: boolean;
  HTTPEndpoint: string;
  HTTPAuthToken: string;
  UniSMS: { AccessKeyID: string; AccessKeySecret: string; Signature: string; TemplateID: string };
  Aliyun: { AccessKeyID: string; AccessKeySecret: string; SignName: string; TemplateCode: string; TemplateParam: string; Region: string };
  TencentCloud: { SecretID: string; SecretKey: string; AppID: string; SignName: string; TemplateID: string; Region: string };
}

// OAuth exchange result - what the backend returns after exchanging code
interface OAuthCallbackResult {
  provider: string;
  provider_subject?: string;
  email?: string;
  nickname?: string;
  next_step?: string;
  oauth_bind_token?: string;
  access_token?: string;
  refresh_token?: string;
  expires_in?: number;
  authorization_token?: string;
  /** @deprecated 同上，恒为 undefined */
  account_id?: number;
  display_id?: string;
  account_type?: string;
  redirect_to?: string;
  must_bind_phone?: boolean;
}

interface ApiResponse<T> {
  code?: number;
  message?: string;
  data?: T;
}

// Admin types
export interface AdminUser {
  id: number;
  /** 对外公开 ID（U0XXXXXX），由 id 派生 */
  display_id?: string;
  email: string;
  phone: string;
  nickname: string | null;
  role: string;
  status: string;
  created_at: string;
  /** 软删时间；仅回收站视图（deleted=only/include）里的已删用户存在 */
  deleted_at?: string;
}

export interface AdminUserDetail {
  id: number;
  /** 对外公开 ID（U0XXXXXX），由 id 派生 */
  display_id?: string;
  email: string;
  phone: string;
  nickname: string | null;
  role: string;
  status: string;
  two_factor_enabled: boolean;
  created_at: string;
  updated_at: string;
}

export interface AdminUserListParams {
  search?: string;
  role?: string;
  status?: string;
  /** 软删可见性："only"=回收站、"include"=全部（含已删）、不传=只看正常用户 */
  deleted?: string;
  page?: number;
  page_size?: number;
}

export interface AdminUserListResult {
  users: AdminUser[];
  count: number;
  page: number;
  total_pages: number;
}

export interface PlatformRegistration {
  id: number;
  platform_id: string;
  platform_name: string;
  registered_at: string;
  registration_ip: string;
  last_login_at: string;
  login_count: number;
  is_active: boolean;
}

export interface PlatformBan {
  id: number;
  platform_id: string;
  platform_name: string;
  reason: string;
  ban_type: string;
  banned_at: string;
  banned_until: string | null;
  banned_by: string;
  is_lifted: boolean;
  lifted_at: string | null;
  lifted_by: string | null;
}


export interface PlatformRegistrationDetail {
  id: number;
  user_id: number;
  user_nickname: string;
  user_email: string;
  user_phone: string;
  platform_id: string;
  platform_name: string;
  registered_at: string;
  registration_ip: string;
  last_login_at: string;
  login_count: number;
  is_active: boolean;
}

export interface PlatformBanDetail {
  id: number;
  user_id: number;
  user_nickname: string;
  user_email: string;
  platform_id: string;
  platform_name: string;
  reason: string;
  ban_type: string;
  banned_at: string;
  banned_until: string;
  banned_by_name: string;
  is_lifted: boolean;
  lifted_at: string;
  lifted_by_name: string;
}

export interface Platform {
  id: number;
  code: string;
  name: string;
  description: string;
  status: string;
  max_quota: number;
  current_quota: number;
  create_time: string;
  update_time: string;
}

export interface OIDCApp {
  client_id: string;
  client_secret?: string;
  name: string;
  redirect_uris: string[];
  scopes: string[];
  status: string;
  remark?: string;
}

export interface SessionInfo {
  id: number;
  login_type: string;
  provider: string | null;
  ip: string | null;
  user_agent: string | null;
  device_info: Record<string, unknown> | null;
  login_at: string;
  expire_at: string | null;
  status: string;
}

export interface CreatePlatformRequest {
  code: string;
  name: string;
  description?: string;
  status?: string;
  max_quota?: number;
}

export interface UpdatePlatformRequest {
  name?: string;
  description?: string;
  status?: string;
  max_quota?: number;
}


// User Agreement types
export interface UserAgreement {
  id: number;
  platform_code: string;
  version: string;
  agreement_type: 'terms_of_service' | 'privacy_policy' | 'user_contract';
  title: string;
  content: string;
  language: string;
  is_active: boolean;
  is_latest: boolean;
  effective_date: string | null;
  expiry_date: string | null;
  created_by: number | null;
  created_by_name: string | null;
  create_time: string;
  update_time: string;
}

export interface CreateAgreementRequest {
  platform_code: string;
  version: string;
  agreement_type: 'terms_of_service' | 'privacy_policy' | 'user_contract';
  title: string;
  content: string;
  language?: string;
  is_active?: boolean;
  is_latest?: boolean;
  effective_date?: string;
  expiry_date?: string;
  metadata?: Record<string, unknown>;
  remark?: string;
}

export interface UpdateAgreementRequest {
  title?: string;
  content?: string;
  is_active?: boolean;
  is_latest?: boolean;
  effective_date?: string;
  expiry_date?: string;
  metadata?: Record<string, unknown>;
  remark?: string;
}

const ERROR_MAP: Record<string, string> = {
  'invalid credentials': '账号或密码错误',
  'unauthorized': '未授权，请重新登录',
  'user is banned or disabled': '账号已被禁用',
  'invalid sms code': '验证码错误或已过期',
  'sms cooldown active': '发送过于频繁，请稍后再试',
  'invalid email code': '验证码错误或已过期',
  'email cooldown active': '发送过于频繁，请稍后再试',
  'invalid two factor code': '两步验证码错误',
  'duplicate oauth identity': '该第三方账号已绑定其他用户',
  'invalid oauth state': 'OAuth 状态无效',
  'invalid oauth bind token': '绑定令牌无效',
  'duplicate phone': '该手机号已被注册',
  'SMS verification failed': '短信验证失败',
  'Session expired': '登录已过期，请重新登录',
};

export function translateError(msg: string): string {
  if (!msg) return '';
  const lower = msg.toLowerCase();
  for (const [en, zh] of Object.entries(ERROR_MAP)) {
    if (lower.includes(en.toLowerCase())) return zh;
  }
  return msg;
}

class ApiClient {
  private baseURL: string;

  constructor(baseURL: string) {
    this.baseURL = baseURL;
  }

  private getHeaders(includeAuth = false): HeadersInit {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };

    if (includeAuth) {
      const token = localStorage.getItem('auth_token');
      if (token && token !== "undefined" && token !== "null") {
        headers['Authorization'] = `Bearer ${token}`;
      }
    }

    return headers;
  }

  private async ensureValidToken(): Promise<void> {
    if (authUtils.isTokenExpiringSoon()) {
      const ok = await authUtils.refreshAccessToken();
      if (!ok) {
        authUtils.logout();
        window.location.href = "/login";
      }
    }
  }

  private async handleResponse<T>(response: Response, retryFn: () => Promise<T>): Promise<T> {
    if (response.status === 401) {
      const body = await response.json().catch(() => ({ message: '' }));
      const rawMsg = body.message || body.error_description || '';
      const errorMsg = translateError(rawMsg) || '认证失败，请检查账号密码';

      // Only attempt refresh if we have a refresh token (i.e. user was logged in)
      if (authUtils.getRefreshToken()) {
        const ok = await authUtils.refreshAccessToken();
        if (ok) {
          return retryFn();
        }
        authUtils.logout();
        window.location.href = "/login";
      }
      throw new Error(errorMsg);
    }
    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Network error' }));
      const rawMsg = error.error_description || error.message || error.msg || '';
      const msg = translateError(rawMsg) || `请求失败 (${response.status})`;
      throw new Error(msg);
    }
    return response.json();
  }

  async post<T>(path: string, data: unknown, includeAuth = false): Promise<T> {
    if (includeAuth) await this.ensureValidToken();
    const doRequest = async () => {
      const response = await fetch(`${this.baseURL}${path}`, {
        method: 'POST',
        headers: this.getHeaders(includeAuth),
        body: JSON.stringify(data),
      });
      return this.handleResponse<T>(response, doRequest);
    };
    return doRequest();
  }

  async put<T>(path: string, data: unknown, includeAuth = true): Promise<T> {
    if (includeAuth) await this.ensureValidToken();
    const doRequest = async () => {
      const response = await fetch(`${this.baseURL}${path}`, {
        method: 'PUT',
        headers: this.getHeaders(includeAuth),
        body: JSON.stringify(data),
      });
      return this.handleResponse<T>(response, doRequest);
    };
    return doRequest();
  }

  async patch<T>(path: string, data: unknown, includeAuth = true): Promise<T> {
    if (includeAuth) await this.ensureValidToken();
    const doRequest = async () => {
      const response = await fetch(`${this.baseURL}${path}`, {
        method: 'PATCH',
        headers: this.getHeaders(includeAuth),
        body: JSON.stringify(data),
      });
      return this.handleResponse<T>(response, doRequest);
    };
    return doRequest();
  }

  async delete<T>(path: string, includeAuth = true): Promise<T> {
    if (includeAuth) await this.ensureValidToken();
    const doRequest = async () => {
      const response = await fetch(`${this.baseURL}${path}`, {
        method: 'DELETE',
        headers: this.getHeaders(includeAuth),
      });
      return this.handleResponse<T>(response, doRequest);
    };
    return doRequest();
  }

  async get<T>(path: string, includeAuth = true): Promise<T> {
    if (includeAuth) await this.ensureValidToken();
    const doRequest = async () => {
      const response = await fetch(`${this.baseURL}${path}`, {
        method: 'GET',
        headers: this.getHeaders(includeAuth),
      });
      return this.handleResponse<T>(response, doRequest);
    };
    return doRequest();
  }

  // Auth endpoints
  async login(email: string, password: string): Promise<LoginResponse> {
    const result = await this.post<ApiResponse<LoginResponse>>('/api/v1/user/auth/login', {
      account: email,
      password,
    });

    if ('data' in result && result.data) {
      return result.data;
    }
    return result as unknown as LoginResponse;
  }

  async register(email: string, password: string, _nickname?: string): Promise<LoginResponse> {
    const result = await this.post<ApiResponse<LoginResponse>>('/api/v1/user/auth/register', {
      account: email,
      password,
    });

    if ('data' in result && result.data) {
      return result.data;
    }
    return result as unknown as LoginResponse;
  }

  /**
   * 登录即注册 - 尝试登录，如果用户不存在则自动注册
   * Login with auto-registration - tries login first, registers if user doesn't exist
   */
  async loginOrRegister(email: string, password: string): Promise<LoginResponse & { is_new_user: boolean }> {
    try {
      // 先尝试登录
      const result = await this.login(email, password);
      return { ...result, is_new_user: false };
    } catch (err: unknown) {
      // 如果是用户不存在的错误，自动注册
      const error = err as Error;
      if (error.message?.includes('不存在') || error.message?.includes('not found') || error.message?.includes('账号') || error.message?.includes('用户')) {
        const result = await this.register(email, password);
        return { ...result, is_new_user: true };
      }
      throw err;
    }
  }

  /**
   * 手机号登录即注册 - 尝试登录，如果用户不存在则自动注册
   */
  async phoneLoginOrRegister(phone: string, password: string): Promise<LoginResponse & { is_new_user: boolean }> {
    try {
      // 先尝试登录
      const result = await this.phonePasswordLogin(phone, password);
      return { ...result, is_new_user: false };
    } catch (err: unknown) {
      // 如果是用户不存在的错误，提示用户使用短信注册
      const error = err as Error;
      if (error.message?.includes('不存在') || error.message?.includes('not found') || error.message?.includes('账号') || error.message?.includes('用户')) {
        throw new Error('手机号未注册，请使用短信验证码登录');
      }
      throw err;
    }
  }

  // Phone authentication methods
  async sendSMS(phone: string, scene: 'login' | 'register' = 'login'): Promise<{ expires_in: number; debug_code?: string }> {
    const { verifyCaptcha } = await import('./captcha');
    const captcha = await verifyCaptcha();
    const result = await this.post<ApiResponse<{ expires_in: number; debug_code?: string }>>(
      '/api/v1/user/sms/send',
      { phone, scene, ...captcha },
      true
    );
    if ('data' in result && result.data) {
      return result.data;
    }
    return result as unknown as { expires_in: number; debug_code?: string };
  }

  async phonePasswordLogin(phone: string, password: string): Promise<LoginResponse> {
    const result = await this.post<ApiResponse<LoginResponse>>(
      '/api/v1/user/auth/phone/password-login',
      { phone, password }
    );
    if ('data' in result && result.data) {
      return result.data;
    }
    return result as unknown as LoginResponse;
  }

  async phoneSMSLogin(phone: string, smsCode: string): Promise<LoginResponse> {
    const result = await this.post<ApiResponse<LoginResponse>>(
      '/api/v1/user/auth/phone/sms-login',
      { phone, sms_code: smsCode }
    );
    if ('data' in result && result.data) {
      return result.data;
    }
    return result as unknown as LoginResponse;
  }

  async phoneRegister(phone: string, smsCode: string, password: string, nickname?: string): Promise<LoginResponse> {
    const result = await this.post<ApiResponse<LoginResponse>>(
      '/api/v1/user/auth/phone/register',
      { phone, sms_code: smsCode, password, nickname }
    );
    if ('data' in result && result.data) {
      return result.data;
    }
    return result as unknown as LoginResponse;
  }


  // Email verification methods
  async sendEmail(email: string): Promise<{ expires_in: number; debug_code?: string }> {
    const { verifyCaptcha } = await import('./captcha');
    const captcha = await verifyCaptcha();
    const result = await this.post<ApiResponse<{ expires_in: number; debug_code?: string }>>(
      '/api/v1/user/email/send',
      { email, ...captcha }
    );
    if ('data' in result && result.data) {
      return result.data;
    }
    return result as unknown as { expires_in: number; debug_code?: string };
  }

  async emailCodeLogin(email: string, code: string): Promise<LoginResponse> {
    const result = await this.post<ApiResponse<LoginResponse>>(
      '/api/v1/user/auth/email/code-login',
      { email, code }
    );
    if ('data' in result && result.data) {
      return result.data;
    }
    return result as unknown as LoginResponse;
  }

  async emailRegister(email: string, code: string, password?: string, nickname?: string): Promise<LoginResponse> {
    const result = await this.post<ApiResponse<LoginResponse>>(
      '/api/v1/user/auth/email/register',
      { email, code, password, nickname }
    );
    if ('data' in result && result.data) {
      return result.data;
    }
    return result as unknown as LoginResponse;
  }

  // OAuth methods
  async getOAuthUrl(provider: string, popup = false, bind = false, authRequestId?: string): Promise<{ authorize_url: string }> {
    const params = new URLSearchParams();
    if (popup) params.set("popup", "1");
    if (bind) params.set("bind", "1");
    if (authRequestId) params.set("auth_request_id", authRequestId);
    const qs = params.toString();
    const result = await this.get<ApiResponse<{ authorize_url: string }>>(
      `/api/v1/user/oauth/${provider}/authorize${qs ? "?" + qs : ""}`
    );
    if ('data' in result && result.data) {
      return result.data;
    }
    return result as unknown as { authorize_url: string };
  }

  // OAuth code exchange - frontend sends code from callback, backend returns JWT
  async exchangeOAuthCode(provider: string, code: string, state: string): Promise<OAuthCallbackResult> {
    const result = await this.post<ApiResponse<OAuthCallbackResult>>(
      `/api/v1/user/oauth/${provider}/exchange`,
      { code, state }
    );
    if ('data' in result && result.data) {
      return result.data;
    }
    return result as unknown as OAuthCallbackResult;
  }

  // Complete OAuth signup - create new account with OAuth bind token
  async completeOAuthSignup(params: {
    oauth_bind_token: string;
    account: string;
    password: string;
    phone: string;
    code: string;
  }): Promise<LoginResponse> {
    const result = await this.post<ApiResponse<LoginResponse>>(
      '/api/v1/user/oauth/complete-signup',
      params
    );
    if ('data' in result && result.data) {
      return result.data;
    }
    return result as unknown as LoginResponse;
  }

  // Bind OAuth to existing account
  async bindOAuthExisting(params: {
    oauth_bind_token: string;
    account: string;
    password: string;
  }): Promise<LoginResponse> {
    const result = await this.post<ApiResponse<LoginResponse>>(
      '/api/v1/user/oauth/bind-existing',
      params
    );
    if ('data' in result && result.data) {
      return result.data;
    }
    return result as unknown as LoginResponse;
  }

  async getIdentities(): Promise<{ provider: string; provider_subject: string; provider_email?: string; created_at: string }[]> {
    const result = await this.get<ApiResponse<{ provider: string; provider_subject: string; provider_email?: string; created_at: string }[]>>(
      '/api/v1/user/identities'
    );
    if ('data' in result && result.data) {
      return result.data;
    }
    return result as unknown as { provider: string; provider_subject: string; provider_email?: string; created_at: string }[];
  }

  async unlinkIdentity(provider: string): Promise<{ ok: boolean }> {
    const result = await this.post<ApiResponse<{ ok: boolean }>>(
      `/api/v1/user/identities/${provider}/unbind`,
      {},
      true
    );
    if ('data' in result && result.data) {
      return result.data;
    }
    return result as unknown as { ok: boolean };
  }

  // Profile endpoints
  async getProfile(): Promise<{ id?: number; display_id?: string; nickname: string; email: string; phone: string; avatar_svg: string; avatar_url?: string; role: string; created_at: string }> {
    const result = await this.get<ApiResponse<{ id?: number; display_id?: string; nickname: string; email: string; phone: string; avatar_svg: string; avatar_url?: string; role: string; created_at: string }>>(
      '/api/v1/user/profile'
    );
    if ('data' in result && result.data) {
      return result.data;
    }
    return result as unknown as { id?: number; display_id?: string; nickname: string; email: string; phone: string; avatar_svg: string; avatar_url?: string; role: string; created_at: string };
  }

  // 只支持改昵称：邮箱换绑走 changeEmail（/user/email/change 验证码流程）。
  async updateProfile(data: { nickname?: string }): Promise<{ id: number; nickname: string; email: string; phone: string; avatar_svg: string; avatar_url?: string; role: string; created_at: string }> {
    const result = await this.put<ApiResponse<{ id: number; nickname: string; email: string; phone: string; avatar_svg: string; avatar_url?: string; role: string; created_at: string }>>(
      '/api/v1/user/profile', data
    );
    if ('data' in result && result.data) {
      return result.data;
    }
    return result as unknown as { id: number; nickname: string; email: string; phone: string; avatar_svg: string; avatar_url?: string; role: string; created_at: string };
  }

  // Phone bind/unbind/change
  async sendPhoneCode(): Promise<{ expires_in: number; debug_code?: string }> {
    const result = await this.post<ApiResponse<{ expires_in: number; debug_code?: string }>>(
      '/api/v1/user/phone/send-code', {}, true
    );
    if ('data' in result && result.data) return result.data;
    return result as unknown as { expires_in: number; debug_code?: string };
  }

  // Check if phone is available for binding
  async checkPhoneAvailability(phone: string): Promise<{ available: boolean }> {
    const result = await this.post<ApiResponse<{ available: boolean }>>(
      '/api/v1/user/phone/check',
      { phone },
      true
    );
    if ('data' in result && result.data) return result.data;
    return result as unknown as { available: boolean };
  }

  async bindPhone(phone: string, smsCode: string): Promise<{ id: number; nickname: string; email: string; phone: string; avatar_svg: string; avatar_url?: string; role: string }> {
    const result = await this.post<ApiResponse<{ id: number; nickname: string; email: string; phone: string; avatar_svg: string; avatar_url?: string; role: string }>>(
      '/api/v1/user/phone/bind', { phone, sms_code: smsCode }, true
    );
    if ('data' in result && result.data) return result.data;
    return result as unknown as { id: number; nickname: string; email: string; phone: string; avatar_svg: string; avatar_url?: string; role: string };
  }

  async unbindPhone(smsCode: string): Promise<{ id: number; nickname: string; email: string; phone: string; avatar_svg: string; avatar_url?: string; role: string }> {
    const result = await this.post<ApiResponse<{ id: number; nickname: string; email: string; phone: string; avatar_svg: string; avatar_url?: string; role: string }>>(
      '/api/v1/user/phone/unbind', { sms_code: smsCode }, true
    );
    if ('data' in result && result.data) return result.data;
    return result as unknown as { id: number; nickname: string; email: string; phone: string; avatar_svg: string; avatar_url?: string; role: string };
  }

  async changePhone(newPhone: string, newSmsCode: string): Promise<{ id: number; nickname: string; email: string; phone: string; avatar_svg: string; avatar_url?: string; role: string }> {
    const result = await this.post<ApiResponse<{ id: number; nickname: string; email: string; phone: string; avatar_svg: string; avatar_url?: string; role: string }>>(
      '/api/v1/user/phone/change', { new_phone: newPhone, new_sms_code: newSmsCode }, true
    );
    if ('data' in result && result.data) return result.data;
    return result as unknown as { id: number; nickname: string; email: string; phone: string; avatar_svg: string; avatar_url?: string; role: string };
  }

  async changeEmail(email: string, code: string): Promise<{ id: number; nickname: string; email: string; phone: string; avatar_svg: string; avatar_url?: string; role: string }> {
    const result = await this.post<ApiResponse<{ id: number; nickname: string; email: string; phone: string; avatar_svg: string; avatar_url?: string; role: string }>>(
      '/api/v1/user/email/change', { email, code }, true
    );
    if ('data' in result && result.data) return result.data;
    return result as unknown as { id: number; nickname: string; email: string; phone: string; avatar_svg: string; avatar_url?: string; role: string };
  }

  async uploadAvatar(file: File): Promise<{ avatar_url: string }> {
    const formData = new FormData();
    formData.append('avatar', file);
    const token = localStorage.getItem('auth_token');
    const resp = await fetch('/api/v1/user/avatar', {
      method: 'POST',
      headers: token ? { 'Authorization': `Bearer ${token}` } : {},
      body: formData,
    });
    const result = await resp.json();
    if (result.code !== 0 && result.code !== 200) throw new Error(result.message || '上传失败');
    return result.data || result;
  }

  async setAvatarURL(avatarURL: string): Promise<{ avatar_url: string }> {
    const result = await this.post<ApiResponse<{ avatar_url: string }>>(
      '/api/v1/user/avatar/url', { avatar_url: avatarURL }
    );
    if ('data' in result && result.data) return result.data;
    return result as unknown as { avatar_url: string };
  }

  // Admin endpoints
  async getAdminUsers(params?: AdminUserListParams): Promise<AdminUserListResult> {
    const searchParams = new URLSearchParams();
    if (params?.search) searchParams.append("search", params.search);
    if (params?.role) searchParams.append("role", params.role);
    if (params?.status) searchParams.append("status", params.status);
    if (params?.deleted) searchParams.append("deleted", params.deleted);
    if (params?.page) searchParams.append("page", String(params.page));
    if (params?.page_size) searchParams.append("page_size", String(params.page_size));

    const qs = searchParams.toString();
    const result = await this.get<ApiResponse<AdminUserListResult>>('/api/v1/admin/users' + (qs ? '?' + qs : ''));
    if ('data' in result && result.data) {
      return result.data;
    }
    return result as unknown as AdminUserListResult;
  }

  async getUserDetail(userId: number): Promise<AdminUserDetail> {
    const result = await this.get<ApiResponse<AdminUserDetail>>(`/api/v1/admin/users/${userId}`);
    if ('data' in result && result.data) {
      return result.data;
    }
    return result as unknown as AdminUserDetail;
  }

  async updateUser(userId: number, data: { nickname?: string; email?: string; phone?: string }): Promise<{ message: string; user: AdminUserDetail }> {
    const result = await this.put<ApiResponse<{ message: string; user: AdminUserDetail }>>(`/api/v1/admin/users/${userId}`, data, true);
    if ('data' in result && result.data) {
      return result.data;
    }
    return result as unknown as { message: string; user: AdminUserDetail };
  }

  async updateUserRole(userId: number, role: string): Promise<{ message: string }> {
    const result = await this.put<ApiResponse<{ message: string }>>(`/api/v1/admin/users/${userId}/role`, { role }, true);
    if ('data' in result && result.data) {
      return result.data;
    }
    return result as unknown as { message: string };
  }

  async updateUserStatus(userId: number, status: string): Promise<{ message: string }> {
    const result = await this.put<ApiResponse<{ message: string }>>(`/api/v1/admin/users/${userId}/status`, { status }, true);
    if ('data' in result && result.data) {
      return result.data;
    }
    return result as unknown as { message: string };
  }

  // 删除用户：hard=false 软删（admin 可用），hard=true 永久删除（super_admin）
  async deleteUser(userId: number, hard = false): Promise<{ message: string; result?: { mode: string; identities: number; registrations: number; bans: number } }> {
    const result = await this.delete<ApiResponse<{ message: string; result?: { mode: string; identities: number; registrations: number; bans: number } }>>(`/api/v1/admin/users/${userId}${hard ? '?hard=true' : ''}`);
    if ('data' in result && result.data) {
      return result.data;
    }
    return result as unknown as { message: string };
  }

  // 回收站恢复：把软删用户恢复为正常账号（邮箱/手机号被别的活跃账号占用时后端返回 409）
  async restoreUser(userId: number): Promise<{ message: string; result?: { id: number; email: string; phone: string } }> {
    const result = await this.post<ApiResponse<{ message: string; result?: { id: number; email: string; phone: string } }>>(`/api/v1/admin/users/${userId}/restore`, {}, true);
    if ('data' in result && result.data) {
      return result.data;
    }
    return result as unknown as { message: string };
  }

  // SMS provider config (admin). Note: backend JSON uses Go field names (capitalized).
  async getSMSConfig(): Promise<SMSConfigResponse> {
    const result = await this.get<ApiResponse<SMSConfigResponse>>('/api/v1/admin/settings/sms', true);
    if ('data' in result && result.data) return result.data;
    return result as unknown as SMSConfigResponse;
  }
  async updateSMSConfig(config: Partial<SMSConfigResponse>): Promise<{ message: string }> {
    const result = await this.put<ApiResponse<{ message: string }>>('/api/v1/admin/settings/sms', config, true);
    if ('data' in result && result.data) return result.data;
    return result as unknown as { message: string };
  }
  async testSMS(phone: string): Promise<{ message: string }> {
    const result = await this.post<ApiResponse<{ message: string }>>('/api/v1/admin/settings/sms/test', { phone }, true);
    if ('data' in result && result.data) return result.data;
    return result as unknown as { message: string };
  }

  async getUserRegistrations(userId: number): Promise<{ user_id: number; registrations: PlatformRegistration[] }> {
    const result = await this.get<ApiResponse<{ user_id: number; registrations: PlatformRegistration[] }>>(`/api/v1/admin/users/${userId}/registrations`);
    if ('data' in result && result.data) {
      return result.data;
    }
    return result as unknown as { user_id: number; registrations: PlatformRegistration[] };
  }

  async getUserBans(userId: number, includeLifted = false): Promise<{ user_id: number; bans: PlatformBan[] }> {
    const result = await this.get<ApiResponse<{ user_id: number; bans: PlatformBan[] }>>(`/api/v1/admin/users/${userId}/bans?include_lifted=${includeLifted}`);
    if ('data' in result && result.data) {
      return result.data;
    }
    return result as unknown as { user_id: number; bans: PlatformBan[] };
  }

  async createBan(userId: number, data: {
    platform_id: string;
    platform_name: string;
    reason: string;
    ban_type: 'permanent' | 'temporary';
    banned_until?: string;
    banned_by: number;
    banned_by_name: string;
  }): Promise<{ message: string }> {
    const result = await this.post<ApiResponse<{ message: string }>>(`/api/v1/admin/users/${userId}/bans`, data, true);
    if ('data' in result && result.data) {
      return result.data;
    }
    return result as unknown as { message: string };
  }

  // liftedBy 可省略：登录响应不再下发自增 id，后端会用 token 里的管理员补齐
  async liftBan(banId: number, liftedBy?: number, liftedByName?: string): Promise<{ message: string }> {
    const result = await this.put<ApiResponse<{ message: string }>>(
      `/api/v1/admin/users/bans/${banId}/lift`,
      { lifted_by: liftedBy, lifted_by_name: liftedByName },
      true
    );
    if ('data' in result && result.data) {
      return result.data;
    }
    return result as unknown as { message: string };
  }



  async getPlatformRegistrations(platformId: string): Promise<{ platform_id: string; registrations: PlatformRegistrationDetail[] }> {
    const result = await this.get<ApiResponse<{ platform_id: string; registrations: PlatformRegistrationDetail[] }>>("/api/v1/admin/platform/" + platformId + "/registrations");
    if ('data' in result && result.data) { return result.data; }
    return result as unknown as { platform_id: string; registrations: PlatformRegistrationDetail[] };
  }

  async getPlatformBans(platformId: string, includeLifted?: boolean): Promise<{ platform_id: string; bans: PlatformBanDetail[] }> {
    const result = await this.get<ApiResponse<{ platform_id: string; bans: PlatformBanDetail[] }>>("/api/v1/admin/platform/" + platformId + "/bans?include_lifted=" + String(includeLifted || false));
    if ('data' in result && result.data) { return result.data; }
    return result as unknown as { platform_id: string; bans: PlatformBanDetail[] };
  }
  // Platform CRUD endpoints
  async listPlatforms(params?: { status?: string; limit?: number }): Promise<{ platforms: Platform[]; count: number }> {
    const searchParams = new URLSearchParams();
    if (params?.status) searchParams.append("status", params.status);
    if (params?.limit) searchParams.append("limit", String(params.limit));

    const queryString = searchParams.toString();
    const result = await this.get<{ code: number; message: string; data: { platforms: Platform[]; count: number } }>("/api/v1/admin/platforms" + (queryString ? "?" + queryString : ""));
    // Unwrap the data field from the response
    if ('data' in result && result.data) {
      return result.data;
    }
    return result as unknown as { platforms: Platform[]; count: number };
  }

  async getPlatform(id: number): Promise<Platform> {
    const result = await this.get<{ code: number; message: string; data: Platform }>(`/api/v1/admin/platforms/${id}`);
    if ('data' in result && result.data) {
      return result.data;
    }
    return result as unknown as Platform;
  }

  async createPlatform(data: CreatePlatformRequest): Promise<Platform> {
    const result = await this.post<{ code: number; message: string; data: Platform }>("/api/v1/admin/platforms", data, true);
    if ('data' in result && result.data) {
      return result.data;
    }
    return result as unknown as Platform;
  }

  async updatePlatform(id: number, data: UpdatePlatformRequest): Promise<Platform> {
    const result = await this.put<{ code: number; message: string; data: Platform }>(`/api/v1/admin/platforms/${id}`, data, true);
    if ('data' in result && result.data) {
      return result.data;
    }
    return result as unknown as Platform;
  }

  async deletePlatform(id: number): Promise<{ message: string; id: number }> {
    return this.delete<{ message: string; id: number }>(`/api/v1/admin/platforms/${id}`, true);
  }

  // API Key management (OIDC Clients)
  async getAPIKeys(): Promise<{ items: OIDCApp[] }> {
    const result = await this.get<ApiResponse<{ items: OIDCApp[] }>>("/api/v1/admin/apps", true);
    return result.data || { items: [] };
  }

  async getPlatformAPIKeys(platformCode: string): Promise<OIDCApp[]> {
    const result = await this.get<ApiResponse<OIDCApp[]>>(`/api/v1/admin/apps/by-platform?platform_code=${platformCode}`, true);    return result.data || [];
  }

  async createAPIKey(data: {
    platform_code: string;
    name?: string;
    redirect_uris?: string[];
    scopes?: string[];
    remark?: string;
  }): Promise<OIDCApp> {
    const result = await this.post<ApiResponse<OIDCApp>>("/api/v1/admin/apps", data, true);
    return result.data!;
  }

  async rotateAPIKeySecret(clientId: string): Promise<OIDCApp> {
    const result = await this.post<ApiResponse<OIDCApp>>(`/api/v1/admin/apps/${clientId}/rotate-secret`, {}, true);
    return result.data!;
  }

  async disableAPIKey(clientId: string): Promise<OIDCApp> {
    const result = await this.post<ApiResponse<OIDCApp>>(`/api/v1/admin/apps/${clientId}/disable`, {}, true);
    return result.data!;
  }

  async enableAPIKey(clientId: string): Promise<OIDCApp> {
    const result = await this.post<ApiResponse<OIDCApp>>(`/api/v1/admin/apps/${clientId}/enable`, {}, true);
    return result.data!;
  }

  async updateAPIKey(clientId: string, data: {
    name?: string;
    redirect_uris?: string[];
    remark?: string;
  }): Promise<OIDCApp> {
    const result = await this.patch<ApiResponse<OIDCApp>>(`/api/v1/admin/apps/${clientId}`, data, true);
    return result.data!;
  }

  // Agreement endpoints
  async getAgreements(params?: {
    platform_code?: string;
    agreement_type?: string;
    language?: string;
    is_active?: boolean;
    limit?: number;
    offset?: number;
  }): Promise<{ agreements: UserAgreement[]; count?: number }> {
    const searchParams = new URLSearchParams();
    if (params?.platform_code) searchParams.append("platform_code", params.platform_code);
    if (params?.agreement_type) searchParams.append("agreement_type", params.agreement_type);
    if (params?.language) searchParams.append("language", params.language);
    if (params?.is_active !== undefined) searchParams.append("is_active", String(params.is_active));
    if (params?.limit) searchParams.append("limit", String(params.limit));
    if (params?.offset) searchParams.append("offset", String(params.offset));

    const queryString = searchParams.toString();
    const result = await this.get<{ code: number; message: string; data: { agreements: UserAgreement[]; count: number } }>("/api/v1/admin/agreements" + (queryString ? "?" + queryString : ""));
    // Unwrap the data field from the response
    if ('data' in result && result.data) {
      return { agreements: result.data.agreements, count: result.data.count };
    }
    return result as unknown as { agreements: UserAgreement[] };
  }

  async getAgreementById(id: number): Promise<UserAgreement> {
    const result = await this.get<{ code: number; message: string; data: UserAgreement }>("/api/v1/agreement/" + id, false);
    if ('data' in result && result.data) {
      return result.data;
    }
    return result as unknown as UserAgreement;
  }

  async getLatestAgreement(platformCode: string, agreementType?: string, language?: string): Promise<UserAgreement> {
    const searchParams = new URLSearchParams({ platform_code: platformCode });
    if (agreementType) searchParams.append("agreement_type", agreementType);
    if (language) searchParams.append("language", language);

    const q = searchParams.toString();
    const result = await this.get<{ code: number; message: string; data: UserAgreement }>("/api/v1/agreement/latest?" + q, false);
    if ('data' in result && result.data) {
      return result.data;
    }
    return result as unknown as UserAgreement;
  }

  async createAgreement(data: CreateAgreementRequest): Promise<{ message: string; agreement: UserAgreement }> {
    const result = await this.post<{ code: number; message: string; data: { message: string; agreement: UserAgreement } }>("/api/v1/admin/agreements", data, true);
    if ('data' in result && result.data) {
      return result.data;
    }
    return result as unknown as { message: string; agreement: UserAgreement };
  }

  async updateAgreement(id: number, data: UpdateAgreementRequest): Promise<{ message: string; agreement: UserAgreement }> {
    const result = await this.put<{ code: number; message: string; data: { message: string; agreement: UserAgreement } }>("/api/v1/admin/agreements/" + id, data, true);
    if ('data' in result && result.data) {
      return result.data;
    }
    return result as unknown as { message: string; agreement: UserAgreement };
  }

  async deleteAgreement(id: number): Promise<{ message: string }> {
    const result = await this.delete<{ code: number; message: string; data: { message: string } }>("/api/v1/admin/agreements/" + id, true);
    if ('data' in result && result.data) {
      return result.data;
    }
    return result as unknown as { message: string };
  }

  async activateAgreement(id: number): Promise<{ message: string }> {
    const result = await this.put<{ code: number; message: string; data: { message: string } }>("/api/v1/admin/agreements/" + id + "/activate", {}, true);
    if ('data' in result && result.data) {
      return result.data;
    }
    return result as unknown as { message: string };
  }

  async deactivateAgreement(id: number): Promise<{ message: string }> {
    const result = await this.put<{ code: number; message: string; data: { message: string } }>("/api/v1/admin/agreements/" + id + "/deactivate", {}, true);
    if ('data' in result && result.data) {
      return result.data;
    }
    return result as unknown as { message: string };
  }

  async setAgreementAsLatest(id: number): Promise<{ message: string }> {
    const result = await this.put<{ code: number; message: string; data: { message: string } }>("/api/v1/admin/agreements/" + id + "/set-latest", {}, true);
    if ('data' in result && result.data) {
      return result.data;
    }
    return result as unknown as { message: string };
  }

  // Sessions & Logout
  async getSessions(): Promise<{ sessions: SessionInfo[]; count: number }> {
    const result = await this.get<{ code: number; data: { sessions: SessionInfo[]; count: number } }>("/api/v1/user/sessions");
    return result.data;
  }

  async revokeSession(sessionId: number): Promise<{ ok: boolean }> {
    const result = await this.post<{ code: number; data: { ok: boolean } }>(`/api/v1/user/sessions/${sessionId}/revoke`, {});
    return result.data;
  }

  async logoutFromServer(refreshToken?: string): Promise<void> {
    try {
      await this.post("/api/v1/user/logout", refreshToken ? { refresh_token: refreshToken } : {});
    } catch {
      // Ignore - clear local state anyway
    }
  }
}

export const api = new ApiClient(API_BASE_URL);


// 用户信息接口
interface UserInfo {
  /** @deprecated 自增 id，普通用户登录已拿不到（可枚举、不外发）；仅极少数后台操作残留使用 */
  id?: number;
  /** 对外公开 ID（U0XXXXXX），用户中心会话的唯一标识 */
  display_id?: string;
  email: string;
  nickname?: string;
  role: UserRole | string;
}

// Auth utilities
export const authUtils = {
  setToken(token: string) {
    if (!token || token === "undefined" || token === "null") {
      console.error("Attempted to set invalid token:", token);
      return;
    }
    localStorage.setItem("auth_token", token);
  },

  setRefreshToken(refreshToken: string, expiresIn: number) {
    if (refreshToken) {
      localStorage.setItem("refresh_token", refreshToken);
    }
    if (expiresIn > 0) {
      localStorage.setItem("token_expires_at", String(Date.now() + expiresIn * 1000));
    }
  },

  getRefreshToken(): string | null {
    return localStorage.getItem("refresh_token");
  },

  getTokenExpiresAt(): number | null {
    const val = localStorage.getItem("token_expires_at");
    return val ? Number(val) : null;
  },

  isTokenExpiringSoon(): boolean {
    const expiresAt = this.getTokenExpiresAt();
    if (!expiresAt) return false;
    return Date.now() > expiresAt - 5 * 60 * 1000; // 5 min buffer
  },

  async refreshAccessToken(): Promise<boolean> {
    const refreshToken = this.getRefreshToken();
    if (!refreshToken) return false;

    try {
      const apiBase = getApiBaseUrl();
      const resp = await fetch(`${apiBase}/api/v1/oauth2/token`, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams({
          grant_type: "refresh_token",
          refresh_token: refreshToken,
        }).toString(),
      });
      if (!resp.ok) return false;
      const data = await resp.json();
      // OIDC token endpoint returns flat JSON (no data wrapper)
      const tokenData = data.data || data;
      if (!tokenData?.access_token) return false;

      this.setToken(tokenData.access_token);
      this.setRefreshToken(tokenData.refresh_token || "", tokenData.expires_in || 0);
      return true;
    } catch {
      return false;
    }
  },

  getToken(): string | null {
    return localStorage.getItem("auth_token");
  },

  removeToken() {
    localStorage.removeItem("auth_token");
    localStorage.removeItem("refresh_token");
    localStorage.removeItem("token_expires_at");
  },

  isAuthenticated(): boolean {
    return this.getToken() !== null;
  },

  setUser(userInfo: UserInfo) {
    localStorage.setItem("user_info", JSON.stringify(userInfo));
    if (userInfo.role === "admin" || userInfo.role === "super_admin") {
      localStorage.setItem("admin_token", this.getToken()!);
    }
  },

  getUser(): UserInfo | null {
    const data = localStorage.getItem("user_info");
    return data ? JSON.parse(data) : null;
  },

  getUserRole(): UserRole | null {
    const user = this.getUser();
    return (user?.role as UserRole) || null;
  },

  isAdmin(): boolean {
    const role = this.getUserRole();
    return role === "admin" || role === "super_admin";
  },

  isSuperAdmin(): boolean {
    return this.getUserRole() === "super_admin";
  },

  setAdminUser(user: { id: number; email: string; nickname?: string }) {
    localStorage.setItem("admin_user", JSON.stringify(user));
  },

  getAdminUser(): { id: number; email: string; nickname?: string } | null {
    const data = localStorage.getItem("admin_user");
    return data ? JSON.parse(data) : null;
  },

  clearAdminUser() {
    localStorage.removeItem("admin_user");
  },

  logout() {
    this.removeToken();
    this.clearAdminUser();
    localStorage.removeItem("user_info");
    localStorage.removeItem("admin_token");
    localStorage.removeItem("user_email");
    localStorage.removeItem("user_nickname");
    localStorage.removeItem("refresh_token");
    localStorage.removeItem("token_expires_at");
  },
};

